import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Badge, Card, Empty, ErrorNote, Spinner } from "@/components/ui";
import { api } from "@/lib/api";
import { ENGAGEMENT_STATUS_LABEL, ENGAGEMENT_STATUS_ORDER, humanise } from "@/lib/dealStages";
import { formatDue, formatMoneyCents } from "@/lib/format";
import { cn } from "@/lib/utils";

/** §7.6 engagement board by status, overdue highlighted. */
export function Engagements() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["engagements"],
    queryFn: () => api.engagements(),
  });

  if (isLoading) return <Spinner />;
  if (error) return <div className="p-4"><ErrorNote error={error} /></div>;
  if (!data) return null;

  const overdue = data.filter((e) => e.overdue);

  return (
    <div className="p-4">
      <header className="mb-3">
        <h1 className="text-lg font-semibold tracking-tight">Engagements</h1>
        <p className="text-xs text-fg-muted">
          {data.length} total
          {overdue.length > 0 && (
            <>
              {" · "}
              <span className="text-danger">{overdue.length} overdue</span>
            </>
          )}
        </p>
      </header>

      {data.length === 0 ? (
        <Card>
          <Empty title="No engagements yet." hint="Add one from a deal's Engagements tab." />
        </Card>
      ) : (
        <div className="flex gap-2 overflow-x-auto pb-2" data-testid="engagement-board">
          {ENGAGEMENT_STATUS_ORDER.map((statusKey) => {
            const inLane = data.filter((e) => e.status === statusKey);
            if (inLane.length === 0 && ["rejected", "cancelled"].includes(statusKey)) return null;
            return (
              <div
                key={statusKey}
                data-testid={`engagement-lane-${statusKey}`}
                className="flex w-56 shrink-0 flex-col rounded-[var(--radius-card)] border bg-surface"
              >
                <header className="flex items-center justify-between border-b px-2 py-1.5">
                  <span className="text-xs font-semibold">
                    {ENGAGEMENT_STATUS_LABEL[statusKey]}
                  </span>
                  <span className="text-[11px] tabular-nums text-fg-muted">{inLane.length}</span>
                </header>
                <ul className="flex-1 space-y-1 p-1.5">
                  {inLane.map((e) => (
                    <li key={e.id}>
                      <div
                        className={cn(
                          "rounded border bg-bg p-2",
                          e.overdue && "border-danger/50 bg-danger/5",
                        )}
                        data-testid={e.overdue ? "engagement-card-overdue" : "engagement-card"}
                      >
                        <p className="text-xs font-medium">{humanise(e.service)}</p>
                        <p className="text-[11px] text-fg-muted">{e.vendor_name}</p>
                        {e.deal_id && (
                          <Link
                            to={`/deals/${e.deal_id}`}
                            className="block truncate text-[11px] underline"
                          >
                            {e.deal_name}
                          </Link>
                        )}
                        {!e.deal_id && e.lead_id && (
                          // v2.1 T6.2: ordered while the land was still a lead.
                          <Link
                            to={`/leads/${e.lead_id}`}
                            className="block truncate text-[11px] underline"
                            data-testid="engagement-lead-link"
                          >
                            on a lead
                          </Link>
                        )}
                        <div className="mt-1 flex flex-wrap items-center gap-1">
                          {e.overdue && <Badge tone="danger">overdue</Badge>}
                          {e.due_at && (
                            <span
                              className={cn(
                                "text-[11px] tabular-nums",
                                e.overdue ? "text-danger" : "text-fg-muted",
                              )}
                            >
                              {formatDue(e.due_at)}
                            </span>
                          )}
                          {e.agreed_cents !== null && (
                            <span className="ml-auto text-[11px] tabular-nums text-fg-muted">
                              {formatMoneyCents(e.agreed_cents)}
                            </span>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
